// Generate card sound effects via Web Audio API (no external assets needed).
// All play* functions accept a `vol` argument (0..1) that scales the peak gain.
let _ctx = null;
const getCtx = () => {
  if (!_ctx) {
    try {
      _ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      _ctx = null;
    }
  }
  return _ctx;
};

// Resume context on first user interaction (browser autoplay policy)
export const unlockAudio = () => {
  const ctx = getCtx();
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
};

// ----- Embed mute (per-document) -----
// The hero tablet renders the demo inside a same-origin iframe. That iframe has
// its OWN copy of this module, so flipping this flag there keeps the demo SILENT
// without ever touching the user's global (localStorage) sound preference.
let _embedMuted = false;
export const setEmbedMuted = (v) => { _embedMuted = !!v; };

// ----- Global Mute Toggle (persisted in localStorage) -----
const MUTED_KEY = "supertmatik_sound_muted";
const _listeners = new Set();

export const getMuted = () => {
  try {
    return localStorage.getItem(MUTED_KEY) === "1";
  } catch (_) {
    return false;
  }
};

export const setMuted = (val) => {
  try {
    localStorage.setItem(MUTED_KEY, val ? "1" : "0");
  } catch (_) { /* noop */ }
  _listeners.forEach((fn) => { try { fn(!!val); } catch (_) { /* noop */ } });
};

export const toggleMuted = () => {
  const next = !getMuted();
  setMuted(next);
  return next;
};

export const subscribeMuted = (fn) => {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
};

const noiseBuffer = (ctx, duration, decay = 0.05) => {
  const buf = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * decay));
  }
  return buf;
};

const playSwipe = (ctx, vol = 1) => {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, 0.25, 0.06);
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(800, ctx.currentTime);
  filter.frequency.exponentialRampToValueAtTime(2400, ctx.currentTime + 0.15);
  filter.Q.value = 4;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.35 * vol, ctx.currentTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
  src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
  src.start();
  src.stop(ctx.currentTime + 0.3);
};

const playFlip = (ctx, vol = 1) => {
  [0, 0.06].forEach((t) => {
    const osc = ctx.createOscillator();
    osc.type = "square";
    const gain = ctx.createGain();
    osc.frequency.setValueAtTime(800, ctx.currentTime + t);
    osc.frequency.exponentialRampToValueAtTime(180, ctx.currentTime + t + 0.05);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
    gain.gain.exponentialRampToValueAtTime(0.22 * vol, ctx.currentTime + t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.08);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(ctx.currentTime + t);
    osc.stop(ctx.currentTime + t + 0.1);
  });
};

const playClick = (ctx, vol = 1) => {
  const osc = ctx.createOscillator();
  osc.type = "triangle";
  const gain = ctx.createGain();
  osc.frequency.setValueAtTime(1200, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.05);
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.25 * vol, ctx.currentTime + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
  osc.connect(gain); gain.connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.1);
};

const playShuffle = (ctx, vol = 1) => {
  [0, 0.08, 0.16, 0.24].forEach((t) => {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, 0.1, 0.03);
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 1500 + Math.random() * 1000;
    filter.Q.value = 5;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
    gain.gain.exponentialRampToValueAtTime(0.25 * vol, ctx.currentTime + t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.1);
    src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
    src.start(ctx.currentTime + t);
    src.stop(ctx.currentTime + t + 0.12);
  });
};

// NEW: deal — quick sharp slap (single card placed on table)
const playDeal = (ctx, vol = 1) => {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, 0.15, 0.02);
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 800;
  filter.Q.value = 2;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.5 * vol, ctx.currentTime + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
  src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
  src.start();
  src.stop(ctx.currentTime + 0.18);
};

// NEW: draw — paper-sliding sound, longer than swipe
const playDraw = (ctx, vol = 1) => {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, 0.45, 0.12);
  const filter = ctx.createBiquadFilter();
  filter.type = "highpass";
  filter.frequency.value = 1600;
  filter.Q.value = 2;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.28 * vol, ctx.currentTime + 0.05);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.42);
  src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
  src.start();
  src.stop(ctx.currentTime + 0.5);
};

// NEW: whoosh — descending filtered noise (card flying past)
const playWhoosh = (ctx, vol = 1) => {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, 0.35, 0.1);
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(3000, ctx.currentTime);
  filter.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.3);
  filter.Q.value = 3;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.4 * vol, ctx.currentTime + 0.05);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
  src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
  src.start();
  src.stop(ctx.currentTime + 0.4);
};

// NEW: tap — short percussive tap
const playTap = (ctx, vol = 1) => {
  const osc = ctx.createOscillator();
  osc.type = "sine";
  const gain = ctx.createGain();
  osc.frequency.setValueAtTime(600, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.04);
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.4 * vol, ctx.currentTime + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.06);
  osc.connect(gain); gain.connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.08);
};

// NEW: slap — harder percussive impact (cards slammed on table)
const playSlap = (ctx, vol = 1) => {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx, 0.08, 0.015);
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 400;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.6 * vol, ctx.currentTime + 0.002);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
  src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
  src.start();
  src.stop(ctx.currentTime + 0.1);
};

// NEW: riffle — many rapid clicks like a deck being riffled
const playRiffle = (ctx, vol = 1) => {
  for (let i = 0; i < 14; i++) {
    const t = i * 0.025;
    const osc = ctx.createOscillator();
    osc.type = "square";
    const gain = ctx.createGain();
    osc.frequency.value = 600 + Math.random() * 800;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
    gain.gain.exponentialRampToValueAtTime(0.15 * vol, ctx.currentTime + t + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.018);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(ctx.currentTime + t);
    osc.stop(ctx.currentTime + t + 0.02);
  }
};

// NEW: bounce — playful boing
const playBounce = (ctx, vol = 1) => {
  const osc = ctx.createOscillator();
  osc.type = "sine";
  const gain = ctx.createGain();
  osc.frequency.setValueAtTime(220, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.06);
  osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.18);
  gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.32 * vol, ctx.currentTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
  osc.connect(gain); gain.connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + 0.3);
};

// NEW realistic-feel "card distribute" — sharp paper-slap with a brief settle
const playRealDeal = (ctx, vol = 1) => {
  // sharp percussion impact
  const impact = ctx.createBufferSource();
  impact.buffer = noiseBuffer(ctx, 0.06, 0.012);
  const imp_filt = ctx.createBiquadFilter();
  imp_filt.type = "bandpass";
  imp_filt.frequency.value = 1800;
  imp_filt.Q.value = 1.2;
  const imp_gain = ctx.createGain();
  imp_gain.gain.setValueAtTime(0.0001, ctx.currentTime);
  imp_gain.gain.exponentialRampToValueAtTime(0.55 * vol, ctx.currentTime + 0.002);
  imp_gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.06);
  impact.connect(imp_filt); imp_filt.connect(imp_gain); imp_gain.connect(ctx.destination);
  impact.start(); impact.stop(ctx.currentTime + 0.08);
  // paper slide settle
  const settle = ctx.createBufferSource();
  settle.buffer = noiseBuffer(ctx, 0.18, 0.07);
  const set_filt = ctx.createBiquadFilter();
  set_filt.type = "highpass";
  set_filt.frequency.value = 1400;
  const set_gain = ctx.createGain();
  set_gain.gain.setValueAtTime(0.0001, ctx.currentTime + 0.02);
  set_gain.gain.exponentialRampToValueAtTime(0.3 * vol, ctx.currentTime + 0.05);
  set_gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
  settle.connect(set_filt); set_filt.connect(set_gain); set_gain.connect(ctx.destination);
  settle.start(ctx.currentTime + 0.02); settle.stop(ctx.currentTime + 0.22);
};

// "Cards" — paper riffle (multiple cards flicked)
const playRealCards = (ctx, vol = 1) => {
  for (let i = 0; i < 6; i++) {
    const t = i * 0.035 + Math.random() * 0.01;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, 0.05, 0.015);
    const filter = ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.value = 1200 + Math.random() * 1500;
    filter.Q.value = 4;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + t);
    gain.gain.exponentialRampToValueAtTime(0.4 * vol, ctx.currentTime + t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.05);
    src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
    src.start(ctx.currentTime + t); src.stop(ctx.currentTime + t + 0.07);
  }
};

// Correct answer — uplifting C-E-G arpeggio
const playCorrect = (ctx, vol = 1) => {
  const notes = [659.25, 783.99, 987.77, 1318.51]; // E5 G5 B5 E6
  notes.forEach((f, i) => {
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = f;
    const gain = ctx.createGain();
    const t0 = ctx.currentTime + i * 0.07;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.35 * vol, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.4);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(t0); osc.stop(t0 + 0.45);
  });
};

// Wrong answer — descending buzz
const playWrong = (ctx, vol = 1) => {
  const osc1 = ctx.createOscillator();
  osc1.type = "sawtooth";
  const g1 = ctx.createGain();
  osc1.frequency.setValueAtTime(220, ctx.currentTime);
  osc1.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.35);
  g1.gain.setValueAtTime(0.0001, ctx.currentTime);
  g1.gain.exponentialRampToValueAtTime(0.35 * vol, ctx.currentTime + 0.02);
  g1.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.38);
  osc1.connect(g1); g1.connect(ctx.destination);
  osc1.start(); osc1.stop(ctx.currentTime + 0.4);
  // beep
  const osc2 = ctx.createOscillator();
  osc2.type = "square";
  osc2.frequency.value = 165;
  const g2 = ctx.createGain();
  g2.gain.setValueAtTime(0.0001, ctx.currentTime + 0.1);
  g2.gain.exponentialRampToValueAtTime(0.18 * vol, ctx.currentTime + 0.11);
  g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
  osc2.connect(g2); g2.connect(ctx.destination);
  osc2.start(ctx.currentTime + 0.1); osc2.stop(ctx.currentTime + 0.3);
};

// Victory — joyful fanfare (3-note triad ascending + ringing chord)
const playVictory = (ctx, vol = 1) => {
  // Fanfare melody C5-E5-G5-C6-E6 staccato
  const notes = [
    [523.25, 0.00], [659.25, 0.10], [783.99, 0.20],
    [1046.5, 0.32], [1318.51, 0.45],
  ];
  notes.forEach(([f, t]) => {
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = f;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
    g.gain.exponentialRampToValueAtTime(0.35 * vol, ctx.currentTime + t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.22);
    osc.connect(g); g.connect(ctx.destination);
    osc.start(ctx.currentTime + t); osc.stop(ctx.currentTime + t + 0.3);
  });
  // Ringing finale chord (C major) — 0.55..1.5s
  [523.25, 659.25, 783.99].forEach((f) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.value = f;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime + 0.55);
    g.gain.exponentialRampToValueAtTime(0.22 * vol, ctx.currentTime + 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.5);
    osc.connect(g); g.connect(ctx.destination);
    osc.start(ctx.currentTime + 0.55); osc.stop(ctx.currentTime + 1.55);
  });
};

// Defeat — sad descending trombone-like wah
const playDefeat = (ctx, vol = 1) => {
  const notes = [
    [392.00, 0.00, 0.30],  // G4
    [349.23, 0.30, 0.35],  // F4
    [311.13, 0.65, 0.40],  // D#4
    [233.08, 1.05, 0.65],  // A#3 — long sad note
  ];
  notes.forEach(([f, t, dur]) => {
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(f, ctx.currentTime + t);
    osc.frequency.exponentialRampToValueAtTime(f * 0.94, ctx.currentTime + t + dur * 0.9);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 1100;
    filter.Q.value = 6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
    g.gain.exponentialRampToValueAtTime(0.28 * vol, ctx.currentTime + t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + dur);
    osc.connect(filter); filter.connect(g); g.connect(ctx.destination);
    osc.start(ctx.currentTime + t); osc.stop(ctx.currentTime + t + dur + 0.05);
  });
};

const playSupert = (ctx, vol = 1) => {
  const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
  notes.forEach((f, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = f;
    const t0 = ctx.currentTime + i * 0.12;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.3 * vol, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.4);
  });
};

// REPEAT sounds — played when player wins an already-owned letter (was_repeat).

// "Oops" — descending two-tone cartoon slide, like a quick "uh-oh"
const playOops = (ctx, vol = 1) => {
  const osc = ctx.createOscillator();
  osc.type = "sine";
  const g = ctx.createGain();
  osc.frequency.setValueAtTime(660, ctx.currentTime);          // "uh"
  osc.frequency.exponentialRampToValueAtTime(520, ctx.currentTime + 0.08);
  osc.frequency.setValueAtTime(440, ctx.currentTime + 0.14);   // "oh"
  osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.35);
  g.gain.setValueAtTime(0.0001, ctx.currentTime);
  g.gain.exponentialRampToValueAtTime(0.32 * vol, ctx.currentTime + 0.02);
  g.gain.setValueAtTime(0.32 * vol, ctx.currentTime + 0.10);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
  // gentle wobble (LFO on gain)
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 6;
  const lfoG = ctx.createGain();
  lfoG.gain.value = 0.05 * vol;
  lfo.connect(lfoG); lfoG.connect(g.gain);
  lfo.start(); lfo.stop(ctx.currentTime + 0.5);
  osc.connect(g); g.connect(ctx.destination);
  osc.start(); osc.stop(ctx.currentTime + 0.5);
};

// "Rrrr" — playful raspberry/buzzer (modulated sawtooth + low filter)
const playRrrr = (ctx, vol = 1) => {
  const osc = ctx.createOscillator();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(160, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(120, ctx.currentTime + 0.5);
  // tremolo (fast amplitude modulation gives the "rrrrrrr" effect)
  const tremolo = ctx.createOscillator();
  tremolo.type = "square";
  tremolo.frequency.value = 28;
  const tremG = ctx.createGain();
  tremG.gain.value = 0.45 * vol;
  const baseG = ctx.createGain();
  baseG.gain.value = 0.22 * vol;
  tremolo.connect(tremG); tremG.connect(baseG.gain);
  // low-pass filter to soften
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 900;
  filter.Q.value = 1.2;
  osc.connect(filter); filter.connect(baseG); baseG.connect(ctx.destination);
  // overall envelope
  baseG.gain.setValueAtTime(0.0001, ctx.currentTime);
  baseG.gain.exponentialRampToValueAtTime(0.28 * vol, ctx.currentTime + 0.04);
  baseG.gain.setValueAtTime(0.28 * vol, ctx.currentTime + 0.45);
  baseG.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
  osc.start(); tremolo.start();
  osc.stop(ctx.currentTime + 0.65); tremolo.stop(ctx.currentTime + 0.65);
};

// "Nope" — short two-beep "no-no" (cartoon negative buzzer)
const playNope = (ctx, vol = 1) => {
  [0, 0.14].forEach((t) => {
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.setValueAtTime(330, ctx.currentTime + t);
    osc.frequency.exponentialRampToValueAtTime(220, ctx.currentTime + t + 0.09);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
    g.gain.exponentialRampToValueAtTime(0.3 * vol, ctx.currentTime + t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.11);
    osc.connect(g); g.connect(ctx.destination);
    osc.start(ctx.currentTime + t); osc.stop(ctx.currentTime + t + 0.13);
  });
};

// "Awww" — disappointed slow descending (e.g., the audience "awwwww")
const playAwww = (ctx, vol = 1) => {
  // 3 voices in parallel forming a minor chord that descends
  const baseFreqs = [330, 415.30, 493.88]; // E4 G#4 B4 — slightly off for "sigh" feel
  baseFreqs.forEach((f, i) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(f, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(f * 0.66, ctx.currentTime + 0.8);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18 * vol, ctx.currentTime + 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.85);
    osc.connect(g); g.connect(ctx.destination);
    osc.start(ctx.currentTime + i * 0.02); osc.stop(ctx.currentTime + 0.95);
  });
};

// BISCAR — realistic tongue-click / lip-pop ("piscar de olho" acoustic
// signature): a very short bass "tock" transient followed by a mid-frequency
// resonance that decays fast, matching the wet-mouth pop humans make.
const playBiscar = (ctx, vol = 1) => {
  const t0 = ctx.currentTime;
  // 1) Sub-bass thump (the low body of the pop). Sine sweep 260Hz → 90Hz in 55ms.
  const sub = ctx.createOscillator();
  sub.type = "sine";
  sub.frequency.setValueAtTime(260, t0);
  sub.frequency.exponentialRampToValueAtTime(90, t0 + 0.055);
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0.0001, t0);
  subGain.gain.exponentialRampToValueAtTime(0.55 * vol, t0 + 0.004);
  subGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.09);
  sub.connect(subGain); subGain.connect(ctx.destination);
  sub.start(t0); sub.stop(t0 + 0.12);

  // 2) Wet-lip transient — brief noise burst through a bandpass around 1.8kHz.
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer(ctx, 0.035, 0.08);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass"; bp.frequency.value = 1800; bp.Q.value = 5.5;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.0001, t0);
  noiseGain.gain.exponentialRampToValueAtTime(0.35 * vol, t0 + 0.003);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
  noise.connect(bp); bp.connect(noiseGain); noiseGain.connect(ctx.destination);
  noise.start(t0); noise.stop(t0 + 0.06);

  // 3) High "tsk" tick — very quick sine chirp 3.2kHz → 1.1kHz to give the
  // signature "click" of the tongue detaching from the palate.
  const tick = ctx.createOscillator();
  tick.type = "triangle";
  tick.frequency.setValueAtTime(3200, t0);
  tick.frequency.exponentialRampToValueAtTime(1100, t0 + 0.03);
  const tickGain = ctx.createGain();
  tickGain.gain.setValueAtTime(0.0001, t0);
  tickGain.gain.exponentialRampToValueAtTime(0.22 * vol, t0 + 0.003);
  tickGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.04);
  tick.connect(tickGain); tickGain.connect(ctx.destination);
  tick.start(t0); tick.stop(t0 + 0.05);
};

const SOUND_FN = {
  swipe: playSwipe,
  flip: playFlip,
  click: playClick,
  shuffle: playShuffle,
  deal: playRealDeal,     // upgraded to realistic deal
  draw: playDraw,
  whoosh: playWhoosh,
  tap: playTap,
  slap: playSlap,
  riffle: playRiffle,
  bounce: playBounce,
  cards: playRealCards,   // NEW realistic multi-card riffle
  correct: playCorrect,   // NEW for correct answers
  wrong: playWrong,       // NEW for wrong answers
  victory: playVictory,   // NEW for game won
  defeat: playDefeat,     // NEW for game lost
  supert: playSupert,
  oops: playOops,
  rrrr: playRrrr,
  nope: playNope,
  awww: playAwww,
  biscar: playBiscar,     // NEW — realistic tongue-click / lip-pop
};

export const playSound = (name, volume = 0.7) => {
  if (!name || name === "none") return;
  if (_embedMuted) return;
  if (getMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  unlockAudio();
  // Clamp volume to 0..1
  const vol = Math.max(0, Math.min(1, Number(volume ?? 0.7)));
  try {
    const fn = SOUND_FN[name];
    if (fn) fn(ctx, vol);
  } catch (e) {
    // ignore
  }
};

export const SOUND_OPTIONS = [
  { value: "deal", label: "Distribuir carta (realista)" },
  { value: "cards", label: "Cartas (riffle realista)" },
  { value: "swipe", label: "Buscar carta (swipe)" },
  { value: "flip", label: "Virar carta (flip)" },
  { value: "shuffle", label: "Embaralhar" },
  { value: "draw", label: "Puxar carta" },
  { value: "whoosh", label: "Whoosh" },
  { value: "click", label: "Click" },
  { value: "tap", label: "Tap" },
  { value: "slap", label: "Slap" },
  { value: "riffle", label: "Riffle rápido" },
  { value: "bounce", label: "Boing" },
  { value: "correct", label: "Acerto (chime arpejo)" },
  { value: "wrong", label: "Erro (buzz)" },
  { value: "victory", label: "Vitória (fanfarra)" },
  { value: "defeat", label: "Derrota (trombone triste)" },
  { value: "oops", label: "Oops (uh-oh cartoon)" },
  { value: "rrrr", label: "Rrrr (raspberry/buzzer)" },
  { value: "nope", label: "Nope (no-no rápido)" },
  { value: "awww", label: "Awww (suspiro)" },
  { value: "biscar", label: "Biscar (piscar de olho — realista)" },
  { value: "none", label: "Sem som" },
];
